// Where the PE section labels go in the plate's right gutter (DESIGN_SPEC §12.15, components .plate-anno).
//   layoutMarks(marks, { rows, padTop, fieldH }) → { label, y }[]   y = % of the field height (the span's top edge)
// Each label starts at its section's row. Labels are vertical 8px mono, so a crowded stretch (.pdata .xdata .idata
// within a few rows) cannot show them all: the header and then the biggest regions claim space first, a label may move
// up or down by at most 1.5× its own length, and labels always stay in file order. Whatever does not fit is left out.
import type { PortraitMark } from "./entropy";

type AnnoLabel = { label: string; y: number };

const ANNO_CHAR_PX = 5.12; // 8px JetBrains Mono advance (.6em) + .04em tracking
const ANNO_PAD_PX = 6; // 3px above and below

export function labelPx(label: string): number {
  return label.length * ANNO_CHAR_PX + ANNO_PAD_PX;
}

export function layoutMarks(
  marks: readonly PortraitMark[],
  opts: { rows: number; padTop?: number; fieldH: number; gap?: number },
): AnnoLabel[] {
  const { rows, fieldH } = opts;
  const padTop = opts.padTop ?? 0;
  const gap = opts.gap ?? 4;
  if (!rows || fieldH <= 0) return [];
  const items = marks
    .map((m) => ({ m, h: labelPx(m.label), want: ((padTop + m.row) / rows) * fieldH }))
    .filter((it) => it.h <= fieldH)
    .sort((a, b) => (a.m.label === "hdr" ? -1 : b.m.label === "hdr" ? 1 : b.m.size - a.m.size || a.m.offset - b.m.offset));
  // kept in file order: a label is only ever placed between its neighbours by offset
  const placed: { label: string; offset: number; top: number; bottom: number }[] = [];
  for (const it of items) {
    const after = placed.findIndex((p) => p.offset > it.m.offset);
    const at = after < 0 ? placed.length : after;
    const lo = at > 0 ? placed[at - 1].bottom + gap : 0;
    const hi = (at < placed.length ? placed[at].top - gap : fieldH) - it.h;
    if (hi < lo) continue;
    const top = Math.min(hi, Math.max(lo, it.want));
    if (Math.abs(top - it.want) > 1.5 * it.h) continue;
    placed.splice(at, 0, { label: it.m.label, offset: it.m.offset, top, bottom: top + it.h });
  }
  return placed.map((p) => ({ label: p.label, y: (p.top / fieldH) * 100 }));
}
